import { useEffect, useRef, useState } from "react";
import { FaDna } from "react-icons/fa";
import { FiAlertCircle, FiFilter } from "react-icons/fi";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioReproducao = () => {
  const [dados, setDados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [matrizes, setMatrizes] = useState([]);
  const [reprodutores, setReprodutores] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [fazendas, setFazendas] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    matrizes: useRef(null),
    reprodutores: useRef(null),
    lotes: useRef(null),
    fazendas: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    matrizes: [],
    reprodutores: [],
    lotes: [],
    fazendas: [],
    data_inicio: "",
    data_fim: "",
    status_gestacao: ""
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [matrizesRes, reprodutoresRes, lotesRes, fazendasRes] = await Promise.all([
        listarTudo("/pecuaria/animal/?sexo=F"),  // Fêmeas para matrizes
        listarTudo("/pecuaria/animal/?sexo=M"),  // Machos para reprodutores
        api.get("/pecuaria/lotes/"),
        api.get("/fazendas/fazendas/")
      ]);

      setMatrizes(Array.isArray(matrizesRes.data) ? matrizesRes.data : matrizesRes.data.results || []);
      setReprodutores(Array.isArray(reprodutoresRes.data) ? reprodutoresRes.data : reprodutoresRes.data.results || []);
      setLotes(lotesRes.data.results || []);
      setFazendas(fazendasRes.data.results || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setMatrizes([]);
      setReprodutores([]);
      setLotes([]);
      setFazendas([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.matrizes && filtros.matrizes.length > 0) {
        params.matrizes = filtros.matrizes.join(",");
      }
      if (filtros.reprodutores && filtros.reprodutores.length > 0) {
        params.reprodutores = filtros.reprodutores.join(",");
      }
      if (filtros.lotes && filtros.lotes.length > 0) {
        params.lotes = filtros.lotes.join(",");
      }
      if (filtros.fazendas && filtros.fazendas.length > 0) {
        params.fazendas = filtros.fazendas.join(",");
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }
      if (filtros.status_gestacao) {
        params.status_gestacao = filtros.status_gestacao;
      }

      console.log("🌐 URL:", `/relatorios/pecuarios/reproducao/?${new URLSearchParams(params)}`);

      const response = await api.get("/relatorios/pecuarios/reproducao/", {
        params: params
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      matrizes: [],
      reprodutores: [],
      lotes: [],
      fazendas: [],
      data_inicio: "",
      data_fim: "",
      status_gestacao: ""
    });
  };

  const exportarExcel = () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dados);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Reproducao"
    );

    XLSX.writeFile(workbook, "relatorio_reproducao.xlsx");
  };

  const exportarPDF = async () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const colunas = [
      { key: "matriz", label: "Matriz" },
      { key: "lote", label: "Lote" },
      { key: "reprodutor", label: "Reprodutor" },
      { key: "tipo_cobertura", label: "Tipo" },
      { key: "data_cobertura", label: "Data Cobertura" },
      { key: "data_diagnostico", label: "Data Diagnóstico" },
      { key: "resultado_diagnostico", label: "Resultado" },
      { key: "data_parto", label: "Data Parto" },
      { key: "dias_gestacao", label: "Dias Gestação" },
      { key: "status", label: "Status" },
      { key: "taxa_prenhez", label: "Taxa Prenhez" },
    ];

    await exportarRelatorioPDF({
      titulo: "Relatório de Reprodução",
      colunas,
      dados,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      formatarValor: (valor) => (valor === null || valor === undefined || valor === "" ? "-" : String(valor)),
    });
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Reproducao</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            .header-info { margin-bottom: 20px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            .stats { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
            .stat-card { background: #f0f0f0; padding: 10px 15px; border-radius: 8px; text-align: center; }
            .stat-card .number { font-size: 24px; font-weight: bold; color: #2c3e50; }
            .stat-card .label { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .prenha { color: green; font-weight: bold; }
            .vazia { color: red; }
            .parida { color: blue; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Reproducao</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total de registros:</strong> ${dados.length}</p>
          </div>

          ${filtros.data_inicio || filtros.data_fim ? `
          <div class="periodo">
            <strong>📅 Periodo analisado:</strong>
            <p>${filtros.data_inicio || 'Sem data inicial'} a ${filtros.data_fim || 'Sem data final'}</p>
          </div>
          ` : ''}

          <div class="stats">
            <div class="stat-card">
              <div class="number">${dados.filter(d => d.status === 'Prenha' || d.resultado_diagnostico === 'Prenha').length}</div>
              <div class="label">Prenhas</div>
            </div>
            <div class="stat-card">
              <div class="number">${dados.filter(d => d.status === 'Vazia' || d.resultado_diagnostico === 'Vazia').length}</div>
              <div class="label">Vazias</div>
            </div>
            <div class="stat-card">
              <div class="number">${dados.filter(d => d.data_parto && d.data_parto !== '-').length}</div>
              <div class="label">Partos</div>
            </div>
            <div class="stat-card">
              <div class="number">${dados.filter(d => d.taxa_prenhez && d.taxa_prenhez !== '-').length > 0 ?
                (dados.reduce((acc, d) => acc + (parseFloat(d.taxa_prenhez) || 0), 0) / dados.filter(d => d.taxa_prenhez && d.taxa_prenhez !== '-').length).toFixed(1) : '0'}%</div>
              <div class="label">Taxa Media Prenhez</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>MATRIZ</th>
                <th>LOTE</th>
                <th>REPRODUTOR</th>
                <th>TIPO</th>
                <th>DATA COBERTURA</th>
                <th>DATA DIAGNOSTICO</th>
                <th>RESULTADO</th>
                <th>DATA PARTO</th>
                <th>DIAS GESTACAO</th>
                <th>TAXA PRENHEZ</th>
              </tr>
            </thead>
            <tbody>
              ${dados.map(item => {
                let statusClass = '';
                if (item.resultado_diagnostico === 'Prenha' || item.status === 'Prenha') statusClass = 'prenha';
                else if (item.resultado_diagnostico === 'Vazia' || item.status === 'Vazia') statusClass = 'vazia';
                else if (item.data_parto && item.data_parto !== '-') statusClass = 'parida';

                return `
                <tr>
                  <td>${item.matriz || '-'}</td>
                  <td>${item.lote || '-'}</td>
                  <td>${item.reprodutor || '-'}</td>
                  <td>${item.tipo_cobertura || '-'}</td>
                  <td>${item.data_cobertura || '-'}</td>
                  <td>${item.data_diagnostico || '-'}</td>
                  <td class="${statusClass}">${item.resultado_diagnostico || item.status || '-'}</td>
                  <td>${item.data_parto || '-'}</td>
                  <td>${item.dias_gestacao || '-'}</td>
                  <td>${item.taxa_prenhez || '-'}%</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp, disabled = false }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome || s.numero_identificacao).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => !disabled && setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {!disabled && openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome || option.numero_identificacao}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={FaDna}
            titulo="Relatório de Reprodução"
            descricao="Consulte o histórico de coberturas, gestações e partos do rebanho"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Matrizes - Multi Select */}
              <MultiSelect
                label="Matrizes"
                options={matrizes}
                selectedValues={filtros.matrizes}
                fieldName="matrizes"
                refProp={dropdownRefs.matrizes}
              />

              {/* Reprodutores - Multi Select */}
              <MultiSelect
                label="Reprodutores"
                options={reprodutores}
                selectedValues={filtros.reprodutores}
                fieldName="reprodutores"
                refProp={dropdownRefs.reprodutores}
              />

              {/* Lotes - Multi Select */}
              <MultiSelect
                label="Lotes"
                options={lotes}
                selectedValues={filtros.lotes}
                fieldName="lotes"
                refProp={dropdownRefs.lotes}
              />

              {/* Fazendas - Multi Select */}
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />

              {/* Status Gestação */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Status da Gestacao
                </label>
                <select
                  name="status_gestacao"
                  value={filtros.status_gestacao}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition bg-white"
                >
                  <option value="">Todas</option>
                  <option value="prenha">Prenhas</option>
                  <option value="vazia">Vazias</option>
                  <option value="parida">Paridas</option>
                </select>
              </div>

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.matrizes && filtros.matrizes.length > 0) ||
              (filtros.reprodutores && filtros.reprodutores.length > 0) ||
              (filtros.lotes && filtros.lotes.length > 0) ||
              (filtros.fazendas && filtros.fazendas.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.matrizes && filtros.matrizes.map((id) => {
                    const matriz = matrizes.find(m => m.id === id);
                    return matriz ? (
                      <span key={`matriz-${id}`} className="bg-pink-100 text-pink-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Matriz: {matriz.numero_identificacao}
                        <button
                          onClick={() => handleMultiSelect("matrizes", id)}
                          className="ml-1 text-pink-600 hover:text-pink-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.reprodutores && filtros.reprodutores.map((id) => {
                    const reprodutor = reprodutores.find(r => r.id === id);
                    return reprodutor ? (
                      <span key={`reprodutor-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Reprodutor: {reprodutor.numero_identificacao}
                        <button
                          onClick={() => handleMultiSelect("reprodutores", id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.lotes && filtros.lotes.map((id) => {
                    const lote = lotes.find(l => l.id === id);
                    return lote ? (
                      <span key={`lote-${id}`} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Lote: {lote.nome}
                        <button
                          onClick={() => handleMultiSelect("lotes", id)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.fazendas && filtros.fazendas.map((id) => {
                    const fazenda = fazendas.find(f => f.id === id);
                    return fazenda ? (
                      <span key={`fazenda-${id}`} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Fazenda: {fazenda.nome}
                        <button
                          onClick={() => handleMultiSelect("fazendas", id)}
                          className="ml-1 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FaDna size={20} className="text-brand-green" />
                  Relatorio de Reproducao
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Tabela */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              {/* Estatísticas */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <CartaoEstatistica
                  rotulo="Prenhas"
                  valor={dados.filter(d => d.status === 'Prenha' || d.resultado_diagnostico === 'Prenha').length}
                  cor="verde"
                />
                <CartaoEstatistica
                  rotulo="Vazias"
                  valor={dados.filter(d => d.status === 'Vazia' || d.resultado_diagnostico === 'Vazia').length}
                  cor="vermelho"
                />
                <CartaoEstatistica
                  rotulo="Partos"
                  valor={dados.filter(d => d.data_parto && d.data_parto !== '-').length}
                  cor="azul"
                />
                <CartaoEstatistica
                  rotulo="Taxa Média Prenhez"
                  valor={`${dados.filter(d => d.taxa_prenhez && d.taxa_prenhez !== '-').length > 0 ?
                    (dados.reduce((acc, d) => acc + (parseFloat(d.taxa_prenhez) || 0), 0) / dados.filter(d => d.taxa_prenhez && d.taxa_prenhez !== '-').length).toFixed(1) : '0'}%`}
                  cor="dourado"
                />
                <CartaoEstatistica
                  rotulo="Total Eventos"
                  valor={dados.length}
                  cor="cinza"
                />
              </div>

              {dados.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200" style={{ maxWidth: '100%' }}>
                  <div style={{ minWidth: 'max-content' }}>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Matriz</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Lote</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Reprodutor</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Data Cobertura</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Data Diagnostico</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Resultado</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Data Parto</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Dias Gestacao</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Taxa Prenhez</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.map((item, index) => {
                          let statusClass = '';
                          if (item.resultado_diagnostico === 'Prenha' || item.status === 'Prenha') statusClass = 'text-green-600 font-bold';
                          else if (item.resultado_diagnostico === 'Vazia' || item.status === 'Vazia') statusClass = 'text-red-600';
                          else if (item.data_parto && item.data_parto !== '-') statusClass = 'text-blue-600';

                          return (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.matriz || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.lote || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.reprodutor || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.tipo_cobertura || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data_cobertura || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data_diagnostico || "-"}</td>
                              <td className={`px-4 py-3 text-sm whitespace-nowrap ${statusClass}`}>
                                {item.resultado_diagnostico || item.status || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data_parto || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.dias_gestacao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.taxa_prenhez || "-"}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioReproducao;
